import React, { useReducer, useEffect, useRef } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useScrollAnimation from "@/hooks/useScrollAnimation";
import { useBooking } from "@/context/BookingContext";
import { useContact } from "@/context/ContactContext";
import { trackEvent } from "@/utils/analytics";
import {
  estimateRecommendation,
  EstimatorAnswers,
  ProjectType,
  Scope,
  Timeline,
  Budget,
} from "@/utils/estimateRecommendation";
import { findPackageBySlug } from "@/data/packages";
import "./EstimatorSection.scss";

type Step = "projectType" | "scope" | "timeline" | "budget" | "result";

interface State {
  step: Step;
  answers: Partial<EstimatorAnswers>;
  opened: boolean;
}

type Action =
  | { type: "OPEN" }
  | { type: "SET_PROJECT_TYPE"; value: ProjectType }
  | { type: "SET_SCOPE"; value: Scope }
  | { type: "SET_TIMELINE"; value: Timeline }
  | { type: "SET_BUDGET"; value: Budget }
  | { type: "BACK" }
  | { type: "RESET" };

const STEP_ORDER: Step[] = ["projectType", "scope", "timeline", "budget", "result"];

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "OPEN":
      return { ...state, step: "projectType", opened: true };
    case "SET_PROJECT_TYPE":
      return { ...state, answers: { ...state.answers, projectType: action.value }, step: "scope" };
    case "SET_SCOPE":
      return { ...state, answers: { ...state.answers, scope: action.value }, step: "timeline" };
    case "SET_TIMELINE":
      return { ...state, answers: { ...state.answers, timeline: action.value }, step: "budget" };
    case "SET_BUDGET":
      return { ...state, answers: { ...state.answers, budget: action.value }, step: "result" };
    case "BACK": {
      const idx = STEP_ORDER.indexOf(state.step);
      if (idx <= 0) return { ...state, step: "projectType" };
      const prev = STEP_ORDER[idx - 1];
      if (prev === "projectType") return { ...state, step: "projectType", answers: {} };
      return { ...state, step: prev };
    }
    case "RESET":
      return { step: "projectType", answers: {}, opened: true };
    default:
      return state;
  }
}

const TOTAL_STEPS = 4;

function stepNumber(step: Step): number {
  const map: Record<Step, number> = {
    projectType: 1,
    scope: 2,
    timeline: 3,
    budget: 4,
    result: 4,
  };
  return map[step] ?? 1;
}

const EstimatorSection: React.FC = () => {
  const { t } = useTranslation();
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });
  const { openBooking } = useBooking();
  const { requestContact } = useContact();
  const resultRef = useRef<HTMLDivElement>(null);

  const [state, dispatch] = useReducer(reducer, {
    step: "projectType",
    answers: {},
    opened: false,
  });

  const { step, answers, opened } = state;

  // Move focus to result card when step becomes "result"
  useEffect(() => {
    if (step === "result" && resultRef.current) {
      resultRef.current.focus();
    }
  }, [step]);

  // Track open on first interaction
  const handleOpen = () => {
    trackEvent("estimator_open");
    dispatch({ type: "OPEN" });
  };

  const handleSelect = (stepKey: Exclude<Step, "result">, value: string) => {
    const num = stepNumber(stepKey);
    trackEvent("estimator_step", { step: String(num) });

    if (stepKey === "projectType") {
      dispatch({ type: "SET_PROJECT_TYPE", value: value as ProjectType });
    } else if (stepKey === "scope") {
      dispatch({ type: "SET_SCOPE", value: value as Scope });
    } else if (stepKey === "timeline") {
      dispatch({ type: "SET_TIMELINE", value: value as Timeline });
    } else if (stepKey === "budget") {
      const nextAnswers = { ...answers, budget: value as Budget } as EstimatorAnswers;
      const result = estimateRecommendation(nextAnswers);
      trackEvent("estimator_complete", { recommendation: result.packageSlug });
      dispatch({ type: "SET_BUDGET", value: value as Budget });
    }
  };

  const handleBack = () => dispatch({ type: "BACK" });
  const handleReset = () => dispatch({ type: "RESET" });

  const renderProgress = (current: number) => (
    <div className="est-progress" aria-label={t("estimator.progress.step", { current, total: TOTAL_STEPS })}>
      {Array.from({ length: TOTAL_STEPS }, (_, i) => (
        <div
          key={i}
          className={`est-progress-dot${i < current ? " est-progress-dot--done" : i === current - 1 ? " est-progress-dot--active" : ""}`}
          aria-hidden="true"
        />
      ))}
      <span className="est-progress-label" aria-live="polite">
        {t("estimator.progress.step", { current, total: TOTAL_STEPS })}
      </span>
    </div>
  );

  const renderOptions = <T extends string>(
    stepKey: Exclude<Step, "result">,
    optionKeys: T[],
    currentValue?: T
  ) => (
    <ul className="est-options">
      {optionKeys.map((key) => (
        <li key={key}>
          <button
            className={`est-option${currentValue === key ? " est-option--selected" : ""}`}
            onClick={() => handleSelect(stepKey, key)}
            aria-pressed={currentValue === key}
          >
            {t(`estimator.options.${stepKey}.${key}`)}
          </button>
        </li>
      ))}
    </ul>
  );

  const renderResult = () => {
    const completeAnswers = answers as EstimatorAnswers;
    const { packageSlug, confidence } = estimateRecommendation(completeAnswers);
    const pkg = findPackageBySlug(packageSlug);
    if (!pkg) return null;

    const pkgContent = t(`packages.items.${pkg.key}`, { returnObjects: true }) as {
      name: string;
      tagline: string;
      priceBand: string;
      priceNote: string;
      timeline: string;
    };

    const whyFitsKey = `estimator.result.whyFits.${packageSlug}.${confidence}`;
    const confidenceLabel = confidence === "fit"
      ? t("estimator.result.confidenceFit")
      : t("estimator.result.confidenceClosest");

    const projectTypeLabel = answers.projectType
      ? t(`estimator.options.projectType.${answers.projectType}`)
      : "";
    const timelineLabel = answers.timeline
      ? t(`estimator.options.timeline.${answers.timeline}`)
      : "";
    const budgetLabel = answers.budget
      ? t(`estimator.options.budget.${answers.budget}`)
      : "";

    const summary = [
      projectTypeLabel && t("estimator.result.summary.project", { value: projectTypeLabel }),
      timelineLabel && t("estimator.result.summary.timeline", { value: timelineLabel }),
      budgetLabel && t("estimator.result.summary.budget", { value: budgetLabel }),
      t("estimator.result.summary.suggested", { name: pkgContent.name, timeline: pkgContent.timeline }),
    ]
      .filter(Boolean)
      .join(" · ");

    const handleBook = () => {
      trackEvent("estimator_cta", { action: "book" });
      openBooking("estimator");
    };

    const handleContact = () => {
      trackEvent("estimator_cta", { action: "contact" });
      requestContact(summary);
    };

    const handleDetails = () => {
      trackEvent("estimator_cta", { action: "details" });
    };

    return (
      <div
        className="est-result"
        ref={resultRef}
        tabIndex={-1}
        aria-live="polite"
        aria-label={t("estimator.result.label")}
      >
        <div className="est-result-badge">{confidenceLabel}</div>
        <span className="est-result-sublabel">{t("estimator.result.label")}</span>
        <h3 className="est-result-name">{pkgContent.name}</h3>
        <p className="est-result-tagline">{pkgContent.tagline}</p>

        <div className="est-result-meta">
          <span className="est-result-price">{pkgContent.priceBand}</span>
          <span className="est-result-dot" aria-hidden="true">·</span>
          <span className="est-result-timeline">{pkgContent.timeline}</span>
        </div>

        <p className="est-result-why">{t(whyFitsKey)}</p>

        <div className="est-result-ctas">
          <button className="est-btn est-btn--primary" onClick={handleBook}>
            {t("estimator.result.cta.book")}
          </button>
          <button className="est-btn est-btn--secondary" onClick={handleContact}>
            {t("estimator.result.cta.contact")}
          </button>
          <RouterLink
            to={`/packages/${packageSlug}`}
            className="est-btn est-btn--ghost"
            onClick={handleDetails}
          >
            {t("estimator.result.cta.details")}
          </RouterLink>
        </div>

        <button className="est-start-over" onClick={handleReset}>
          {t("estimator.startOver")}
        </button>
      </div>
    );
  };

  return (
    <section
      className="estimator-section"
      id="estimator-section"
      ref={ref}
      aria-label={t("estimator.label")}
    >
      <div className="container">
        <div className={`est-head reveal${isVisible ? " in" : ""}`}>
          <span className="sec-label">{t("estimator.label")}</span>
          <h2 className="sec-title">
            {(() => {
              const title = t("estimator.title");
              const hl = t("estimator.titleHighlight");
              const [before, after = ""] = title.split(hl);
              return (
                <>
                  {before}
                  <em>{hl}</em>
                  {after}
                </>
              );
            })()}
          </h2>
          <p className="est-subtitle">{t("estimator.subtitle")}</p>
        </div>

        <div className={`est-card reveal${isVisible ? " in" : ""}`} style={{ transitionDelay: "0.12s" }}>
          {!opened ? (
            <div className="est-intro">
              <button className="est-btn est-btn--primary est-btn--large" onClick={handleOpen}>
                {t("estimator.startCta")}
              </button>
            </div>
          ) : step === "result" ? (
            renderResult()
          ) : (
            <div className="est-step">
              {renderProgress(stepNumber(step))}

              <h3
                className="est-step-title"
                aria-label={t(`estimator.steps.${step}.aria`)}
              >
                {t(`estimator.steps.${step}.title`)}
              </h3>

              {step === "projectType" &&
                renderOptions("projectType", ["aiProduct", "automation", "improve", "notSure"], answers.projectType)}
              {step === "scope" &&
                renderOptions("scope", ["idea", "specs", "running"], answers.scope)}
              {step === "timeline" &&
                renderOptions("timeline", ["asap", "quarter", "exploring"], answers.timeline)}
              {step === "budget" &&
                renderOptions("budget", ["under10k", "10kTo25k", "over25k", "guidance"], answers.budget)}

              {step !== "projectType" && (
                <button className="est-back" onClick={handleBack}>
                  ← {t("estimator.back")}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default EstimatorSection;
